import {
  MouseEvent, useCallback, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { Store, useAppDispatch } from 'src/redux/Store';
import FormDatePicker from 'src/components/Control/FormControls/FormDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import FormSingleSelect, { SelectItem } from 'src/components/Control/FormControls/FormSingleSelect';
import Subtitle from 'src/components/UI/Subtitle';
import Header from 'src/components/UI/Header';
import Row from 'src/components/UI/Row';
import Col2 from 'src/components/UI/Col2';
import Title from 'src/components/UI/Title';
import Button from '@material-ui/core/Button';
import Form from 'src/components/UI/Form';
import Scroller from 'src/components/UI/Scroller';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { t, f } from 'src/lib/language';
import GatewaySelector from 'src/components/Control/GatewaySelector';
import OrganisationBankAccountSelector from 'src/components/Control/OrganisationBankAccountSelector';
import PaymentAccountSelector from 'src/components/Control/PaymentAccountSelector';
import { ProcessingType } from 'src/models/GatewayModel';
import { PAYMENT_TYPE } from 'src/models/PaymentModel';
import { MODALS_SIZE } from 'src/models/modal';
import { closeModal, openModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import {
  SUBSCRIPTION_FREQUENCY,
  SUBSCRIPTION_FREQUENCY_LIST,
  SUBSCRIPTION_STATE,
  SubscriptionModel,
  SubscriptionPayload,
} from 'src/models/SubscriptionModel';
import { getSubscriptionAction, putSubscriptionAction } from 'src/redux/actions/subscriptionActions';
import SubscriptionValidator from 'src/validations/SubscriptionValidator';
import ActionButton, { MENU_BUTTON_CATEGORY } from 'src/components/UI/ActionButton';
import ShowInvoicesForSubscriptionButton
  from 'src/components/Control/Buttons/ShowInvoicesForSubscriptionButton';
import ShowPaymentsForSubscriptionButton
  from 'src/components/Control/Buttons/ShowPaymentsForSubscriptionButton';
import SaveIcon from '@material-ui/icons/Save';
import { Moment } from 'moment';
import { setSelections } from 'src/redux/actions/selectionActions';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';

const buildSubscriptionState = (subscription: SubscriptionModel) => ({
  title: subscription.title || '',
  frequency: (subscription.frequency as SUBSCRIPTION_FREQUENCY) || SUBSCRIPTION_FREQUENCY.monthly,
  start_date: subscription.start_date ? moment(subscription.start_date) : moment() as Moment,
  end_date: subscription.end_date ? moment(subscription.end_date) : null as Moment | null,
  contact_id: subscription.contact_id || 0,
  create_invoice: subscription.create_invoice ?? true,
  create_payment: subscription.create_payment ?? false,
  send_invoice_by_email: subscription.send_invoice_by_email ?? true,
  payment_type: (subscription.payment_type as PAYMENT_TYPE) || PAYMENT_TYPE.cc,
  payment_info_id: subscription.payment_info_id || 0,
  payment_gateway_id: subscription.payment_gateway_id || 0,
  bank_account_id: subscription.bank_account_id || 0,
  state: (subscription.state as SUBSCRIPTION_STATE) || SUBSCRIPTION_STATE.active,
});

const EditSubscriptionForm = () => {
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 680px)' });

  const selections = useSelector(
    (state: Store) => state.selected[ENTITIES.subscriptions] || [],
  );
  const subscription = useSelector(
    (state: Store) => (
      selections[0] && state.subscriptions[selections[0]]
        ? state.subscriptions[selections[0]] as SubscriptionModel
        : {} as SubscriptionModel
    ),
  );

  const [subscriptionState, setSubscriptionState] = useState(
    () => buildSubscriptionState(subscription),
  );

  useEffect(() => {
    dispatch(setError({ [FORM.put_subscription]: {} }));
    if (selections[0]) {
      dispatch(getSubscriptionAction(selections[0] as number));
    }
  }, []);

  const lastBuiltSubscriptionStateSource = useRef('');

  useEffect(() => {
    if (!subscription.id) return;
    const nextSubscriptionState = buildSubscriptionState(subscription);
    const nextSubscriptionStateSource = JSON.stringify(nextSubscriptionState);
    if (nextSubscriptionStateSource === lastBuiltSubscriptionStateSource.current) return;
    lastBuiltSubscriptionStateSource.current = nextSubscriptionStateSource;
    setSubscriptionState(nextSubscriptionState);
  }, [subscription]);

  const processing = useSelector(
    (state: Store) => (
      state.formStatus[FORM.get_subscription] === FORM_STATUS.loading
      || state.formStatus[FORM.put_subscription] === FORM_STATUS.processing
    ),
  );

  const frequencyItems: SelectItem[] = SUBSCRIPTION_FREQUENCY_LIST.map(
    (frequency) => ({ id: frequency, text: frequency }),
  );

  const paymentTypeItems: SelectItem[] = [
    { id: PAYMENT_TYPE.cc, text: PAYMENT_TYPE.cc },
    { id: PAYMENT_TYPE.eft, text: PAYMENT_TYPE.eft },
  ];

  const getGatewayType = (paymentType: PAYMENT_TYPE) => (
    paymentType === PAYMENT_TYPE.cc ? ProcessingType.cc : ProcessingType.eft
  );

  const stateItems: SelectItem[] = [
    { id: SUBSCRIPTION_STATE.active, text: SUBSCRIPTION_STATE.active },
    { id: SUBSCRIPTION_STATE.stopped, text: SUBSCRIPTION_STATE.stopped },
  ];

  const onFieldChange = useCallback((value: Value, field: string) => {
    setSubscriptionState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const onDateChange = (field: string) => (value: MaterialUiPickersDate) => {
    setSubscriptionState((prev) => ({
      ...prev,
      [field]: value ? (value as Moment) : null,
    }));
  };

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.editSubscription }));
  };

  const handleEditTemplateInvoice = () => {
    if (!subscription.invoice_id) return;
    dispatch(setSelections({
      entity: ENTITIES.invoices,
      selections: [subscription.invoice_id],
    }));
    dispatch(openModal({ modal: MODALS.editInvoice }));
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [FORM.put_subscription]: {} }));
    const errorBag = {} as ErrorBag;

    try {
      SubscriptionValidator(lang, false).validateSync(
        {
          ...subscriptionState,
          start_date: subscriptionState.start_date?.format('YYYY-MM-DD'),
          end_date: subscriptionState.end_date ? subscriptionState.end_date.format('YYYY-MM-DD') : null,
        },
        { abortEarly: false },
      );
    } catch (validationErrors: any) {
      (validationErrors?.inner || []).forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
    }

    if (Object.keys(errorBag).length) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: t(lang, 'forms.contact.validation_errors'),
      }));
      dispatch(setError({ [FORM.put_subscription]: errorBag }));
      return false;
    }

    const payload = {
      title: subscriptionState.title,
      frequency: subscriptionState.frequency,
      start_date: subscriptionState.start_date.format('YYYY-MM-DD'),
      end_date: subscriptionState.end_date ? subscriptionState.end_date.format('YYYY-MM-DD') : null,
      create_invoice: subscriptionState.create_invoice,
      create_payment: subscriptionState.create_payment,
      send_invoice_by_email: subscriptionState.send_invoice_by_email,
      payment_type: subscriptionState.create_payment ? subscriptionState.payment_type : null,
      payment_info_id: subscriptionState.create_payment
        ? subscriptionState.payment_info_id || null
        : null,
      payment_gateway_id: subscriptionState.create_payment
        ? subscriptionState.payment_gateway_id || null
        : null,
      currency_id: null,
      bank_account_id: subscriptionState.create_payment
        ? subscriptionState.bank_account_id || null
        : null,
      state: subscriptionState.state,
    } as SubscriptionPayload;

    dispatch(putSubscriptionAction(selections[0], payload));
    return true;
  };

  const renderReadOnlyInfo = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isTabletOrMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: '8px 16px',
      marginBottom: '16px',
    }}>
      <div>
        <b>{f(lang, 'recurrings.next_date')}:</b>&nbsp;{subscription.next_date || '-'}
      </div>
      <div>
        <b>{f(lang, 'recurrings.source')}:</b>&nbsp;{subscription.source || '-'}
      </div>
      <div>
        <b>{f(lang, 'recurrings.last_invoice_status')}:</b>&nbsp;{subscription.last_invoice_status || '-'}
      </div>
      <div>
        <b>{f(lang, 'recurrings.last_payment_status')}:</b>&nbsp;{subscription.last_payment_status || '-'}
      </div>
      <div>
        <b>{f(lang, 'recurrings.invoice_counter')}:</b>&nbsp;{subscription.invoice_counter ?? '-'}
      </div>
      <div>
        <b>{f(lang, 'recurrings.payment_counter')}:</b>&nbsp;{subscription.payment_counter ?? '-'}
      </div>
      <div>
        <b>{f(lang, 'recurrings.invoice_failure_count')}:</b>&nbsp;{subscription.invoice_failure_count ?? '-'}
      </div>
      <div>
        <b>{f(lang, 'recurrings.payment_failure_count')}:</b>&nbsp;{subscription.payment_failure_count ?? '-'}
      </div>
    </div>
  );

  return (
    <>
      <Header height="86px">
        <Row>
          <Title>{t(lang, 'menus.edit_subscription')}</Title>
          <Button
            style={{ marginLeft: 'auto' }}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleClose}>
            {t(lang, 'misc.close')}
          </Button>
        </Row>
        <Row>
          <ActionButton
            id="subscription-save"
            loading={processing}
            category={MENU_BUTTON_CATEGORY.action}
            onClick={handleSubmit}>
            <SaveIcon />
          </ActionButton>
          <ShowInvoicesForSubscriptionButton
            subscriptionId={subscription.id || 0}
            disabled={!subscription.id} />
          <ShowPaymentsForSubscriptionButton
            subscriptionId={subscription.id || 0}
            disabled={!subscription.id} />
        </Row>
      </Header>
      <Scroller style={{ paddingRight: '16px', overflowX: 'hidden' }}>
        <Form maxWidth={MODALS_SIZE.medium}>
          {renderReadOnlyInfo()}
          <Subtitle noMargin style={{ marginBottom: '8px' }}>
            {f(lang, 'recurrings.template_invoice')}
          </Subtitle>
          <Row height="auto" alignItems="center" style={{ marginBottom: '16px' }}>
            <span style={{ marginRight: '16px' }}>
              <b>{f(lang, 'recurrings.amount')}:</b>&nbsp;{subscription.amount ?? '-'}
            </span>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleEditTemplateInvoice}
              disabled={!subscription.invoice_id}>
              {f(lang, 'recurrings.edit_template_invoice')}
            </Button>
          </Row>
          <Row height="72px" alignItems="flex-start">
            <FormTextField
              form={FORM.put_subscription}
              label={f(lang, 'recurrings.title')}
              name="title"
              onChange={onFieldChange}
              required
              value={subscriptionState.title || ''} />
          </Row>
          <Row height="72px" alignItems="flex-start">
            <Col2 minHeight="52px">
              <FormSingleSelect
                form={FORM.put_subscription}
                label={f(lang, 'recurrings.frequency')}
                name="frequency"
                items={frequencyItems}
                value={subscriptionState.frequency}
                noMarginTop
                onChange={(value: SUBSCRIPTION_FREQUENCY) => onFieldChange(value, 'frequency')} />
            </Col2>
          </Row>
          <Row height="72px" alignItems="flex-start">
            <Col2 minHeight="52px">
              <FormDatePicker
                form={FORM.put_subscription}
                required
                label={f(lang, 'recurrings.start_date')}
                name="start_date"
                onChange={onDateChange('start_date')}
                value={subscriptionState.start_date} />
            </Col2>
            <Col2 minHeight="52px">
              <FormDatePicker
                form={FORM.put_subscription}
                label={f(lang, 'recurrings.end_date')}
                name="end_date"
                onChange={onDateChange('end_date')}
                value={subscriptionState.end_date} />
            </Col2>
          </Row>
          <Row height="72px" alignItems="flex-start">
            <Col2 minHeight="52px">
              <FormSingleSelect
                form={FORM.put_subscription}
                label={f(lang, 'recurrings.state')}
                name="state"
                items={stateItems}
                value={subscriptionState.state}
                noMarginTop
                onChange={(value: SUBSCRIPTION_STATE) => onFieldChange(value, 'state')} />
            </Col2>
          </Row>
          <Subtitle noMargin style={{ marginBottom: '8px', marginTop: '16px' }}>
            {f(lang, 'recurrings.state')}
          </Subtitle>
          <Row height="auto">
            <FormCheckbox
              name="create_invoice"
              label={f(lang, 'recurrings.create_invoice')}
              checked={subscriptionState.create_invoice}
              onChange={onFieldChange} />
          </Row>
          <Row height="auto">
            <FormCheckbox
              name="create_payment"
              label={f(lang, 'recurrings.create_payment')}
              checked={subscriptionState.create_payment}
              onChange={onFieldChange} />
          </Row>
          <Row height="auto">
            <FormCheckbox
              name="send_invoice_by_email"
              label={f(lang, 'recurrings.send_invoice_by_email')}
              checked={subscriptionState.send_invoice_by_email}
              onChange={onFieldChange} />
          </Row>
          { subscriptionState.create_payment && (
            <>
              <Subtitle noMargin style={{ marginBottom: '8px', marginTop: '16px' }}>
                {t(lang, 'forms.invoices.payment')}
              </Subtitle>
              <Row height="72px" alignItems="flex-start">
                <Col2 minHeight="52px">
                  <FormSingleSelect
                    form={FORM.put_subscription}
                    label={f(lang, 'recurrings.payment_info')}
                    name="payment_type"
                    items={paymentTypeItems}
                    value={subscriptionState.payment_type}
                    noMarginTop
                    onChange={(value: PAYMENT_TYPE) => onFieldChange(value, 'payment_type')} />
                </Col2>
                <Col2 minHeight="52px">
                  <PaymentAccountSelector
                    value={subscriptionState.payment_info_id || undefined}
                    type={subscriptionState.payment_type}
                    onChange={(id: number) => onFieldChange(id, 'payment_info_id')}
                    form={FORM.put_subscription}
                    name="payment_info_id"
                    gatewayId={subscriptionState.payment_gateway_id || 0}
                    contactId={subscriptionState.contact_id || 1} />
                </Col2>
              </Row>
              <Row height="72px" alignItems="flex-start" style={{ marginTop: '8px' }}>
                <Col2 minHeight="52px">
                  <OrganisationBankAccountSelector
                    value={subscriptionState.bank_account_id || undefined}
                    onChange={(id: number) => onFieldChange(id, 'bank_account_id')}
                    form={FORM.put_subscription}
                    name="bank_account_id"
                    noMarginTop
                    noMinWidth />
                </Col2>
                <Col2 minHeight="52px">
                  <GatewaySelector
                    form={FORM.put_subscription}
                    name="payment_gateway_id"
                    onChange={(value: number) => onFieldChange(value, 'payment_gateway_id')}
                    type={getGatewayType(subscriptionState.payment_type)}
                    value={subscriptionState.payment_gateway_id}
                    noMarginTop
                    noMinWidth />
                </Col2>
              </Row>
            </>
          )}
        </Form>
      </Scroller>
    </>
  );
};

export default EditSubscriptionForm;
//
